/**
 * Product gallery video.
 *
 * Each video slide ships as a responsive poster image plus an empty <video>
 * (snippets/product-media.liquid). Nothing is downloaded until the slide is
 * at least half on screen, and an above-the-fold video waits for the page's
 * load event so it never competes with the first paint. The MP4 rendition is
 * the smallest one that covers the slide at a capped pixel density; slow or
 * Save-Data connections always get the smallest. Playback runs only while the
 * slide is visible and the tab is in front. Save-Data and reduced-motion
 * shoppers get tap-to-play instead of autoplay.
 */
(() => {
  if (window.productVideoReady) return;
  window.productVideoReady = true;

  const connection = navigator.connection || {};
  const lowData = Boolean(connection.saveData) || /2g$/.test(connection.effectiveType || '');
  const slowNetwork = lowData || connection.effectiveType === '3g';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const autoplayAllowed = () => !lowData && !reducedMotion.matches;

  const pageLoaded = document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));

  const bySize = (a, b) => a.w - b.w;

  // Serve renditions from the store's own /cdn/shop path so the request
  // reuses the page's open connection instead of a new one to cdn.shopify.com.
  const sameOrigin = (url) => url.replace(/^https?:\/\/cdn\.shopify\.com\/videos\//, '/cdn/shop/videos/');

  /**
   * Liquid only exposes the largest MP4, while the product .js endpoint lists
   * every rendition. One request per product, shared by all its videos.
   */
  const renditions = new Map();
  const productRenditions = (productUrl) => {
    if (!renditions.has(productUrl)) {
      renditions.set(productUrl, fetch(`${productUrl}.js`, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((product) => new Map((product.media || [])
          .filter((media) => media.media_type === 'video')
          .map((media) => [
            String(media.id),
            (media.sources || [])
              .filter((source) => source.format === 'mp4')
              .map((source) => ({ w: source.width, h: source.height, u: sameOrigin(source.url) }))
              .sort(bySize),
          ])))
        .catch(() => new Map()));
    }
    return renditions.get(productUrl);
  };

  const videoSources = async (wrapper) => {
    let fallback = [];
    try {
      fallback = JSON.parse(wrapper.dataset.sources || '[]').sort(bySize);
    } catch (error) {
      fallback = [];
    }
    if (!wrapper.dataset.productUrl) return fallback;
    const all = await productRenditions(wrapper.dataset.productUrl);
    const sources = all.get(wrapper.dataset.mediaId);
    return sources && sources.length > 0 ? sources : fallback;
  };

  // Retina screens get a sharper file, but not a full 3x one: past ~1.5x the
  // extra megabytes are not visible in a looping product clip.
  const pickSource = (wrapper, sources) => {
    if (slowNetwork) return sources[0];
    const density = Math.min(window.devicePixelRatio || 1, 1.5) * 0.8;
    const needWidth = wrapper.clientWidth * density;
    const needHeight = wrapper.clientHeight * density;
    return sources.find((source) => (
      source.w >= needWidth && source.h >= needHeight
    )) || sources[sources.length - 1];
  };

  const state = new WeakMap();

  const setPlaying = (wrapper, playing) => {
    const toggle = wrapper.querySelector('[data-product-video-toggle]');
    wrapper.toggleAttribute('data-playing', playing);
    if (toggle) {
      toggle.setAttribute('aria-pressed', String(playing));
      toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }
  };

  const play = async (wrapper) => {
    const video = wrapper.querySelector('video');
    const info = state.get(wrapper);
    if (!video || !info) return;
    info.wanted = true;
    if (!video.getAttribute('src')) {
      info.sources = info.sources || videoSources(wrapper);
      const sources = await info.sources;
      if (sources.length === 0) return;
      if (!video.getAttribute('src')) {
        video.preload = 'auto';
        video.src = pickSource(wrapper, sources).u;
      }
    }
    // The shopper may have paused or scrolled away while sources resolved.
    if (!info.wanted) return;
    video.play()?.catch(() => setPlaying(wrapper, false));
  };

  const pause = (wrapper) => {
    const video = wrapper.querySelector('video');
    const info = state.get(wrapper);
    if (info) info.wanted = false;
    if (video && !video.paused) video.pause();
  };

  const visibility = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const wrapper = entry.target;
        const info = state.get(wrapper);
        if (!info) return;
        info.visible = entry.intersectionRatio >= 0.5;
        if (!info.visible) {
          pause(wrapper);
        } else if (autoplayAllowed() && !info.userPaused) {
          pageLoaded.then(() => {
            if (info.visible && !info.userPaused && !document.hidden) play(wrapper);
          });
        }
      });
    }, { threshold: [0, 0.5] })
    : null;

  const registerVideo = (wrapper) => {
    if (state.has(wrapper)) return;
    const video = wrapper.querySelector('video');
    const toggle = wrapper.querySelector('[data-product-video-toggle]');
    if (!video) return;
    const info = {
      visible: false,
      wanted: false,
      userPaused: false,
      sources: null,
    };
    state.set(wrapper, info);

    video.addEventListener('playing', () => {
      wrapper.setAttribute('data-started', '');
      setPlaying(wrapper, true);
    });
    video.addEventListener('pause', () => setPlaying(wrapper, false));

    toggle?.addEventListener('click', (event) => {
      event.preventDefault();
      if (info.wanted) {
        info.userPaused = true;
        pause(wrapper);
      } else {
        info.userPaused = false;
        play(wrapper);
      }
    });

    if (visibility) {
      visibility.observe(wrapper);
    } else if (autoplayAllowed()) {
      pageLoaded.then(() => play(wrapper));
    }
  };

  const registerExternalVideo = (wrapper) => {
    if (state.has(wrapper)) return;
    state.set(wrapper, {});
    wrapper.querySelector('[data-product-video-toggle]')?.addEventListener('click', (event) => {
      event.preventDefault();
      const iframe = document.createElement('iframe');
      iframe.className = 'product__image product__video-frame';
      iframe.src = wrapper.dataset.embedUrl;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.title = wrapper.querySelector('img')?.alt || 'Product video';
      wrapper.replaceChildren(iframe);
    }, { once: true });
  };

  const scan = (scope) => {
    scope.querySelectorAll('[data-product-video]').forEach(registerVideo);
    scope.querySelectorAll('[data-product-external-video]').forEach(registerExternalVideo);
  };

  // Desktop reveals gallery slides in batches from a <template>, and variant
  // changes move slides around, so pick up video slides as they arrive.
  const watchGalleries = () => {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.videoWatched) return;
      gallery.dataset.videoWatched = 'true';
      new MutationObserver(() => scan(gallery)).observe(gallery, { childList: true });
    });
  };

  const init = () => {
    scan(document);
    watchGalleries();
  };

  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('[data-product-video]').forEach((wrapper) => {
      const info = state.get(wrapper);
      if (!info) return;
      // Pages opened in a background tab start here too: the slide was
      // already in view, but playback waited for the tab to come forward.
      if (document.hidden) {
        pause(wrapper);
      } else if (info.visible && !info.userPaused && autoplayAllowed()) {
        pageLoaded.then(() => play(wrapper));
      }
    });
  });

  document.addEventListener('shopify:section:load', init);
  init();
})();
